import { BACKEND_API_BASE_URL } from '../config';

const BASE_URL = BACKEND_API_BASE_URL;

// Helper function to get auth header
const getAuthHeaders = (authToken = null) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  
  return headers;
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  if (response.status === 204) {
    return null; // No content
  }
  return response.json();
};

class PeriodsAPI {
  // Get periods by classroom ID
  async getPeriodsByClassroom(classroomId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/classroom/${classroomId}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching periods by classroom:', error);
      throw error;
    }
  }

  // Get all periods with pagination
  async getAllPeriods(page = 1, limit = 20, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching all periods:', error);
      throw error;
    }
  }

  // Get school timezone information for period operations  
  async getSchoolTimezone(schoolId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/schools/${schoolId}/timezone`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching school timezone:', error);
      throw error;
    }
  }

  // Get a single period by ID
  async getPeriod(periodId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/${periodId}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching period:', error);
      throw error;
    }
  }

  // Create a new period
  async createPeriod(periodData, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify(periodData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error creating period:', error);
      throw error;
    }
  }

  // Update an existing period
  async updatePeriod(periodId, periodData, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/${periodId}`, {
        method: 'PUT',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify(periodData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating period:', error);
      throw error;
    }
  }

  // Delete a period
  async deletePeriod(periodId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/${periodId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting period:', error);
      throw error;
    }
  }

  // Get schedule for a specific day and classroom
  async getScheduleByDay(classroomId, dayOfWeek, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/classroom/${classroomId}/schedule/${dayOfWeek}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching schedule by day:', error);
      throw error;
    }
  }

  // Get current period for a classroom
  async getCurrentPeriod(classroomId, dayOfWeek, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/classroom/${classroomId}/current?day=${dayOfWeek}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching current period:', error);
      throw error;
    }
  }

  // Get next period for a classroom
  async getNextPeriod(classroomId, dayOfWeek, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/classroom/${classroomId}/next?day=${dayOfWeek}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching next period:', error);
      throw error;
    }
  }

  // Assign a teacher to a specific period
  async assignTeacherToPeriod(periodId, syncedTeacherId, authToken = null) {
    try {
      const url = `${BASE_URL}/api/periods/${periodId}/assign-teacher`;
      const body = { synced_teacher_id: syncedTeacherId };
      
      console.log('🔍 periodsAPI.assignTeacherToPeriod:', {
        url,
        method: 'POST',
        body,
        hasToken: !!authToken
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify(body)
      });
      
      console.log('🔍 periodsAPI response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      const result = await handleResponse(response);
      console.log('✅ periodsAPI result:', result);
      return result;
    } catch (error) {
      console.error('❌ Error assigning teacher to period:', error);
      throw error;
    }
  }

  // Remove teacher assignment from a specific period
  async removeTeacherFromPeriod(periodId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/${periodId}/remove-teacher`, {
        method: 'DELETE',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error removing teacher from period:', error);
      throw error;
    }
  }

  // Get all periods assigned to a specific teacher
  async getPeriodsByTeacher(teacherId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/teacher/${teacherId}`, {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching periods by teacher:', error);
      throw error;
    }
  }

  // Assign classroom's default teacher to all periods that don't have a teacher
  async assignDefaultTeacherToAllPeriods(classroomId, authToken = null) {
    try {
      const response = await fetch(`${BASE_URL}/api/periods/classroom/${classroomId}/assign-default-teacher`, {
        method: 'POST',
        headers: getAuthHeaders(authToken)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error assigning default teacher to periods:', error);
      throw error;
    }
  }
}

// Utility functions for working with period data
export const periodUtils = {
  // Convert time string to minutes since midnight
  timeToMinutes: (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  },

  // Convert minutes since midnight to time string
  minutesToTime: (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  },

  // Calculate period duration in minutes
  calculateDuration: (startTime, endTime) => {
    const startMinutes = periodUtils.timeToMinutes(startTime);
    const endMinutes = periodUtils.timeToMinutes(endTime);
    return endMinutes - startMinutes;
  },

  // Format period time for display
  formatPeriodTime: (startTime, endTime) => {
    return `${startTime} - ${endTime}`;
  },

  // Get day of week number (0 = Sunday, 1 = Monday, etc.)
  getCurrentDayOfWeek: () => {
    return new Date().getDay();
  },

  // Convert day number to day name
  dayNumberToName: (dayNumber) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber];
  },

  // Check if current time is within a period
  isCurrentPeriod: (period) => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const startTime = periodUtils.timeToMinutes(period.start_time);
    const endTime = periodUtils.timeToMinutes(period.end_time);
    
    return currentTime >= startTime && currentTime < endTime;
  },

  // Sort periods by start time
  sortPeriodsByTime: (periods) => {
    return periods.sort((a, b) => {
      const aTime = periodUtils.timeToMinutes(a.start_time);
      const bTime = periodUtils.timeToMinutes(b.start_time);
      return aTime - bTime;
    });
  }
};

export const periodsAPI = new PeriodsAPI();
export default periodsAPI;
